import React, { useState } from 'react'
import { Button, Group, Modal, Text, Stack } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppTranslation } from 'hooks/useTranslation'
import { IconPlayerPlay, IconPlayerPause, IconPlayerStop } from '@tabler/icons-react'
import { updateCampaignStatus, validateStatusTransition } from 'supabase/campaigns'
import { Database } from 'types/supabase'

type Campaign = Database['public']['Tables']['campaigns']['Row']
type CampaignStatus = 'Draft' | 'Scheduled' | 'Active' | 'Paused' | 'Ended'

interface CampaignStatusActionsProps {
  campaign: Campaign & { campaign_creatives?: { id: string }[] }
  onStatusChange?: (newStatus: CampaignStatus) => void
}

export const CampaignStatusActions: React.FC<CampaignStatusActionsProps> = ({
  campaign,
  onStatusChange
}) => {
  const { t } = useAppTranslation()
  const queryClient = useQueryClient()
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean
    status: CampaignStatus | null
    message: string
  }>({ open: false, status: null, message: '' })

  const statusMutation = useMutation({
    mutationFn: async (newStatus: CampaignStatus) => {
      return updateCampaignStatus(campaign.id, { status: newStatus })
    },
    onSuccess: (_, newStatus) => {
      notifications.show({
        title: t('campaigns.status.success'),
        message: t('campaigns.status.updatedTo', { status: newStatus }),
        color: 'green'
      })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns-with-impressions'] })
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] })
      if (onStatusChange) {
        onStatusChange(newStatus)
      }
    },
    onError: (error: Error) => {
      notifications.show({
        title: t('campaigns.status.error'),
        message: error.message,
        color: 'red'
      })
    }
  })

  const handleStatusChange = (newStatus: CampaignStatus) => {
    const validation = validateStatusTransition(
      campaign.status as CampaignStatus,
      newStatus
    )

    if (!validation.valid) {
      notifications.show({
        title: t('campaigns.status.invalidTransition'),
        message: validation.message,
        color: 'red'
      })
      return
    }

    // Check for creatives if activating
    if (newStatus === 'Active' && (!campaign.campaign_creatives || campaign.campaign_creatives.length === 0)) {
      setConfirmModal({
        open: true,
        status: newStatus,
        message: t('campaigns.status.noCreativesWarning')
      })
      return
    }

    // Confirm ending campaign
    if (newStatus === 'Ended') {
      setConfirmModal({
        open: true,
        status: newStatus,
        message: t('campaigns.status.confirmEnd')
      })
      return
    }

    statusMutation.mutate(newStatus)
  }

  const getAvailableActions = (): JSX.Element[] => {
    const currentStatus = campaign.status as CampaignStatus
    const actions: JSX.Element[] = []

    switch (currentStatus) {
      case 'Draft':
        actions.push(
          <Button
            key='schedule'
            variant='outline'
            color='blue'
            size='xs'
            onClick={() => handleStatusChange('Scheduled')}
            leftSection={<IconPlayerPlay size={16} />}
          >
            {t('campaigns.status.schedule')}
          </Button>
        )
        break

      case 'Scheduled':
        actions.push(
          <Button
            key='activate'
            variant='outline'
            color='green'
            size='xs'
            onClick={() => handleStatusChange('Active')}
            leftSection={<IconPlayerPlay size={16} />}
            disabled={!campaign.campaign_creatives || campaign.campaign_creatives.length === 0}
          >
            {t('campaigns.status.activate')}
          </Button>
        )
        break

      case 'Active':
        actions.push(
          <Button
            key='pause'
            variant='outline'
            color='yellow'
            size='xs'
            onClick={() => handleStatusChange('Paused')}
            leftSection={<IconPlayerPause size={16} />}
          >
            {t('campaigns.status.pause')}
          </Button>
        )
        break

      case 'Paused':
        actions.push(
          <Button
            key='resume'
            variant='outline'
            color='green'
            size='xs'
            onClick={() => handleStatusChange('Active')}
            leftSection={<IconPlayerPlay size={16} />}
          >
            {t('campaigns.status.resume')}
          </Button>
        )
        break
    }

    // End action available for all except already ended
    if (currentStatus !== 'Ended') {
      actions.push(
        <Button
          key='end'
          variant='outline'
          color='red'
          size='xs'
          onClick={() => handleStatusChange('Ended')}
          leftSection={<IconPlayerStop size={16} />}
        >
          {t('campaigns.status.end')}
        </Button>
      )
    }

    return actions
  }

  return (
    <>
      <Group gap='xs'>
        {getAvailableActions()}
      </Group>

      <Modal
        opened={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, status: null, message: '' })}
        title={t('campaigns.status.confirm')}
      >
        <Stack>
          <Text>{confirmModal.message}</Text>
          <Group justify='flex-end'>
            <Button
              variant='default'
              onClick={() => setConfirmModal({ open: false, status: null, message: '' })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color='red'
              onClick={() => {
                if (confirmModal.status) {
                  statusMutation.mutate(confirmModal.status)
                }
                setConfirmModal({ open: false, status: null, message: '' })
              }}
              loading={statusMutation.isPending}
            >
              {t('common.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}